import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import styles from './collapsibleSection.module.scss';
import i18n from 'i18n';

const cx = classNames.bind(styles);

export type CollapsibleSectionProps = {
  /** 標題列文字（或自訂節點） */
  title: React.ReactNode;
  /** 是否提供縮放功能。false 時內容永遠展開且不顯示縮放箭頭 */
  collapsible?: boolean;
  /** 非受控模式的初始展開狀態（預設展開） */
  defaultExpanded?: boolean;
  /** 受控模式：傳入後由外部控制展開狀態，需搭配 onToggle */
  expanded?: boolean;
  /** 展開狀態切換時的 callback，回傳切換後的狀態 */
  onToggle?: (expanded: boolean) => void;
  /** collapsible 時，展開狀態右側顯示的文字（預設「編輯完成」） */
  expandedActionLabel?: React.ReactNode;
  /** collapsible 時，收合狀態右側顯示的文字（預設「編輯設定」） */
  collapsedActionLabel?: React.ReactNode;
  /** 標題列右側額外內容 */
  headerExtra?: React.ReactNode;
  /** 外層 className */
  className?: string;
} & React.PropsWithChildren;

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  collapsible = true,
  defaultExpanded = true,
  expanded,
  onToggle,
  expandedActionLabel = i18n.t('collapsibleSection.labels.expand'),
  collapsedActionLabel = i18n.t('collapsibleSection.labels.collapse'),
  headerExtra,
  className,
  children
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const isControlled = expanded !== undefined;
  const isExpanded = collapsible
    ? isControlled
      ? expanded
      : internalExpanded
    : true;

  const toggle = () => {
    if (!collapsible) {
      return;
    }
    const next = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onToggle?.(next);
  };

  return (
    <div className={cx('collapsibleSection', className)}>
      <div
        className={cx('header', { clickable: collapsible })}
        onClick={toggle}
      >
        <span className={cx('title', { black: !collapsible })}>{title}</span>
        {(collapsible || headerExtra) && (
          <div className={styles.headerRight}>
            {headerExtra}
            {collapsible && (
              <span className={styles.action}>
                <span className={styles.actionLabel}>
                  {isExpanded ? expandedActionLabel : collapsedActionLabel}
                </span>
                <FontAwesomeIcon
                  className={styles.caret}
                  icon={isExpanded ? faCaretDown : faCaretUp}
                />
              </span>
            )}
          </div>
        )}
      </div>
      {isExpanded && <div className={styles.content}>{children}</div>}
    </div>
  );
};
